import { defineComponent as _defineComponent } from 'vue'
import { onBeforeMount, ref } from 'vue';
import type { ContentCollection, ContentReference } from '@ilteducation/content-collections-types';
import type { CollectionsByIdsSelectorConfig, CollectionsSelectorConfig } from '@ilteducation/content-views-types';
import MovableItem from '@/components/base/MovableItem.vue';
import type { NameId, ResolvedCollection } from '@/resources/views/view-types';
import { getList, getOne } from '@/data-provider/contentCollections';
import eventBus from '@/foundations/services/eventBus';

type Emits = {
  (e: 'update-collection', id: string, config: CollectionsSelectorConfig): void;
};
type Props = {
  id: string;
  config: CollectionsByIdsSelectorConfig;
  realm: string;
  context: string;
};
type CollAndContent = {
  id: string;
  name?: string;
  content?: ContentReference[];
  activeFrom?: string;
  activeTo?: string;
  state?: 'active' | 'disabled';
};


export default /*#__PURE__*/_defineComponent({
  __name: 'CollectionsById',
  props: {
    id: null,
    config: null,
    realm: null,
    context: null
  },
  emits: ["update-collection"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'update-collection', id: string, config: CollectionsSelectorConfig): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  id: string;
  config: CollectionsByIdsSelectorConfig;
  realm: string;
  context: string;
}





const dragging = ref(false);
const componentData = ref({
  props: {
    type: 'transition',
    name: 'flip-list',
  },
});
// Drop down props
const isLoading = ref(false);
const expand = ref(false);
const items = ref<NameId[]>([]);
const itemCursor = ref<string | null | undefined>(undefined);
const selectedColl = ref<NameId>();

const dateLocaleOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const collectionsWithContent = ref<CollAndContent[]>(
  props.config.ids.map((id) => ({
    id: id,
    name: `Collection ${id}`,
    content: [],
  })),
);

const change = () => {
  emits('update-collection', props.id, {
    ids: collectionsWithContent.value.map((a) => a.id),
    type: 'CollectionsByIds',
  });
};

const getCoverUrl = (content: ContentReference) => {
  if (content.type === 'Book') {
    const cfUrl = process.env.VUE_APP_COVERS_DOMAIN;
    return `https://${cfUrl}/bookCovers/${content.id}/cover.png?width=128`;
  }
  return 'https://picsum.photos/200/300';
};

const resolveCollection = async (realm: string, collectionId: string): Promise<ResolvedCollection> => {
  try {
    const coll: ContentCollection = await getOne(realm, collectionId);
    if (coll?.contents.type === 'ContentsByReferences') {
      return {
        state: coll.state,
        name: coll.name,
        content: coll.contents.references,
        activeFrom: coll.activeFrom,
        activeTo: coll.activeTo,
      };
    } else {
      console.debug(`Collection ${collectionId} is not of type ContentsByReferences`, coll.contents.type);
    }
  } catch (err: any) {
    console.error(err);
    eventBus.$emit('snackbarV2.open', { text: `Failed to fetch from collections!`, color: 'error' });
  }

  return { state: 'disabled' , name: 'unknown', content: [] };
};

const fetchItems = async () => {
  try {
    isLoading.value = true;
    do {
      const filter = { realm: props.realm, context: props.context };
      const { data, cursor: c } = await getList(filter, itemCursor.value, 50);
      if (data?.length > 0) {
        const colls: ContentCollection[] = data;
        const cRefs = colls.filter((c) => c.contents.type === 'ContentsByReferences' && c.realm === props.realm);
        items.value = items.value.concat(
          cRefs.map((c) => ({ id: c.id, name: c.name, displayName: `${c.name} - (${c.context})` })),
        );
      }
      itemCursor.value = c ?? null;
    } while (itemCursor.value && itemCursor.value.length > 0);
  } catch (error) {
    itemCursor.value = null;
    console.error(error);
    eventBus.$emit('snackbarV2.open', { text: `Failed to load collections!`, color: 'error' });
  } finally {
    isLoading.value = false;
  }
};

const onUpdateItems = (collections: CollAndContent[]) => {
  collectionsWithContent.value = collections;
  change();
};

const addCollection = async () => {
  if (selectedColl.value?.id) {
    const resolvedCollection = await resolveCollection(props.realm, selectedColl.value.id);
    console.debug(resolvedCollection);
    const { name, content, activeFrom, activeTo, state } = resolvedCollection;
    collectionsWithContent.value.unshift({
      id: selectedColl.value.id,
      name,
      content,
      activeFrom,
      activeTo,
      state,
    });

    change();
  }
};

const populateContent = async () => {
  for (let i = 0; i < props.config.ids.length; i += 1) {
    const id = props.config.ids[i];
    // eslint-disable-next-line no-await-in-loop
    const resolvedCollection = await resolveCollection(props.realm, id);
    const { name, content, activeFrom, activeTo, state } = resolvedCollection;
    const collectionWithContentById = collectionsWithContent.value.find((cc) => cc.id === id);
    if (collectionWithContentById) {
      collectionWithContentById.name = name;
      collectionWithContentById.content = content;
      collectionWithContentById.activeFrom = activeFrom;
      collectionWithContentById.activeTo = activeTo;
      collectionWithContentById.state = state;
    }
  }
};
populateContent();

onBeforeMount(async () => {
  await fetchItems();
});

const isCollectionActive = (coll: CollAndContent): boolean => {
  const now = new Date();

  if (coll.activeFrom && now < new Date(coll.activeFrom)) {
    return false;
  }
  if (coll.activeTo && now > new Date(coll.activeTo)) {
    return false;
  }

  return true;
};

return { __sfc: true,emits, props, dragging, componentData, isLoading, expand, items, itemCursor, selectedColl, dateLocaleOptions, collectionsWithContent, change, getCoverUrl, resolveCollection, fetchItems, onUpdateItems, addCollection, populateContent, isCollectionActive, MovableItem }
}

})